// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { invitationApi } from '../api/invitationApi';
import { profileRequestApi } from '../api/profileRequestApi';
import { useToast } from '@loa/ui-components';
import type { EmailTemplate, ClientInvitationPreview, ClientPolicyInfo } from '../types/invitation';
import { ClientPolicyTable } from '../components/invitation/ClientPolicyTable';
import { EmailComposerPanel } from '../components/email/EmailComposerPanel';
import { RecipientInfoCard } from '../components/email/RecipientInfoCard';
import { PoliciesCard } from '../components/email/PoliciesCard';

export function WizardSendInvitationsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const profileId = searchParams.get('profileId');
  const templateType = searchParams.get('templateType');
  const isAddPolicies = templateType === 'add-policies';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const [defaultTemplate, setDefaultTemplate] = useState<EmailTemplate | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isModified, setIsModified] = useState(false);

  const { showToast } = useToast();
  const [profileName, setProfileName] = useState('');
  const [clients, setClients] = useState<ClientInvitationPreview[]>([]);
  const [policies, setPolicies] = useState<ClientPolicyInfo[]>([]);

  useEffect(() => {
    if (!profileId) {
      setError('Profile ID is required');
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        const preview = isAddPolicies
          ? await invitationApi.getAddPoliciesInvitationPreview(profileId)
          : await invitationApi.getInvitationPreview(profileId);
        setDefaultTemplate(preview.defaultTemplate);
        setSubject(preview.defaultTemplate.subject);
        setBody(preview.defaultTemplate.editableBodyHtml);
        setProfileName(preview.profileName);
        setClients(preview.clients);
        setPolicies(preview.policies || []);
      } catch (err) {
        console.error('Failed to load invitation data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load invitation data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [profileId, isAddPolicies]);

  const handleSubjectChange = useCallback((next: string) => {
    setSubject(next);
    setIsModified(true);
  }, []);

  const handleBodyChange = useCallback((html: string) => {
    setBody(html);
    setIsModified(true);
  }, []);

  const handleReset = useCallback(() => {
    if (defaultTemplate) {
      setSubject(defaultTemplate.subject);
      setBody(defaultTemplate.editableBodyHtml);
      setIsModified(false);
    }
  }, [defaultTemplate]);

  const handleCancel = () => {
    if (profileId) {
      navigate(`/relationships/${profileId}?tab=clients`);
    }
  };

  const handleSendEmail = async () => {
    if (!profileId || clients.length === 0) return;

    setSending(true);
    setSendError(null);

    try {
      const clientNames = clients.map(c => c.clientName).join(', ');

      // Advisor-initiated LOA creation: before dispatching the invitation
      // email, ensure one LOA-typed Profile Request exists per signing
      // policy on the profile. POST /api/requests is per-policy idempotent
      // via LoaRequestHandler.findActiveOnPolicy — repeats return the
      // existing Request, so retries are safe.
      //
      // Only policies with >=1 signatory are eligible — the invitation
      // preview returns only those policies, so we pass them all.
      // Skipped silently for add-policies resends with no new policies.
      if (policies.length > 0) {
        try {
          for (const policy of policies) {
            await profileRequestApi.create({
              profileId,
              requestType: 'LOA',
              policyId: policy.policyId,
              initiatedByType: 'ADVISOR',
            });
          }
        } catch (err) {
          console.error('Failed to create LOA Profile Requests before sending invitations:', err);
          const message = err instanceof Error
            ? err.message
            : 'Failed to prepare the Letter of Authority. Please try again.';
          setSendError(message);
          setSending(false);
          return;
        }
      }

      if (isAddPolicies) {
        // Send add-policies invitation per client
        let failed = 0;
        for (const c of clients) {
          try {
            await invitationApi.sendAddPoliciesInvitation(profileId, c.clientId, {
              customSubject: isModified ? subject : undefined,
              customBody: isModified ? body : undefined,
            });
          } catch {
            failed++;
          }
        }
        if (failed === 0) {
          showToast(
            <>
              <span className="font-bold">Welcome to your client portal</span>
              {' '}email sent to{' '}
              <span className="font-bold">{clientNames}</span>
            </>
          );
          navigate(`/relationships/${profileId}?tab=clients`);
        } else {
          setSendError(`${failed} of ${clients.length} invitations failed to send.`);
        }
      } else {
        const batchClients = clients.map(c => ({
          clientId: c.clientId,
          customSubject: isModified ? subject : undefined,
          customBody: isModified ? body : undefined,
        }));

        const result = await invitationApi.sendInvitationsBatch(profileId, { clients: batchClients });

        if (result.failed === 0) {
          // JOINT relationship: when one or more signatories were deferred
          // (the secondary waiting on the primary), surface that in the toast
          // so the advisor isn't surprised the secondary's email hasn't arrived.
          const deferred = (result.results ?? []).filter(r => r.resultStatus === 'DEFERRED');
          const sentNames = (result.results ?? [])
            .filter(r => r.resultStatus === 'SENT')
            .map(r => r.clientName)
            .join(', ');
          if (deferred.length > 0) {
            const primaryName = deferred[0].deferredUntilClientName ?? 'the primary signatory';
            const deferredNames = deferred.map(r => r.clientName).join(', ');
            showToast(
              <>
                <span className="font-bold">Sign your Letter of Authority Document</span>
                {' '}email sent to{' '}
                <span className="font-bold">{sentNames || primaryName}</span>.{' '}
                <span className="font-bold">{deferredNames}</span> will be notified after{' '}
                <span className="font-bold">{primaryName}</span> signs.
              </>
            );
          } else {
            showToast(
              <>
                <span className="font-bold">Sign your Letter of Authority Document</span>
                {' '}email sent to{' '}
                <span className="font-bold">{clientNames}</span>
              </>
            );
          }
          navigate(`/relationships/${profileId}?tab=clients`);
        } else {
          setSendError(`${result.failed} of ${clients.length} invitations failed to send.`);
        }
      }
    } catch (err) {
      console.error('Failed to send invitations:', err);
      setSendError(err instanceof Error ? err.message : 'Failed to send invitations');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full py-8 px-6">
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-purple-primary border-t-transparent rounded-full animate-spin" />
          <span className="ml-3 text-sm text-grey-02">Loading email template...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full py-8 px-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-2 text-sm text-purple-primary hover:underline"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Backend already scopes JOINT previews to signatory clients ordered
  // primary-first; for SOLO this is the single client. Recipients shows
  // every client that will actually be invited.
  const primaryClient = clients.find(c => c.isPrimary) ?? clients[0];
  const recipientEmails = clients.map(c => c.email).filter(Boolean).join(', ');

  return (
    <EmailComposerPanel
      subject={subject}
      onSubjectChange={handleSubjectChange}
      subjectPlaceholder="Sign your Letter of Authority Document"
      recipients={recipientEmails || primaryClient?.email || ''}
      recipientsPlaceholder="mary.smith@gmail.com"
      body={body}
      onBodyChange={handleBodyChange}
      isModified={isModified}
      onReset={handleReset}
      onCancel={handleCancel}
      onSend={handleSendEmail}
      sending={sending}
      error={sendError}
    >
      <RecipientInfoCard
        title={profileName}
        email={primaryClient?.email}
        phone={primaryClient?.phone}
      />

      {/* Policies requiring signature (hidden for add-policies flow since no policies exist yet) */}
      {!isAddPolicies && policies.length > 0 && (
        <PoliciesCard
          title={`Policies requiring signature (${policies.length})`}
          description="These are the policies clients need to sign a Letter of Authority for."
        >
          <ClientPolicyTable policies={policies} />
        </PoliciesCard>
      )}
    </EmailComposerPanel>
  );
}
